import { useState } from 'react'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { useComando } from './api'
import DialogConferma from './DialogConferma'
import {
  CAPABILITY_IDS,
  formatta,
  formDaCard,
  gruppiCommunity,
  motivoNonOverride,
  payloadRimozioneOverride,
  situazioneCommunity,
  testoCapability,
  testoModalita
} from './Functions'
import SchedaConfigurazione from './SchedaConfigurazione'
import StatoCard from './StatoCard'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Riga di una community: situazione (eredita / fissata / eccezione / non ammessa),
 * comando di eccezione solo se `canOverride`, «Eredita dall'environment» che
 * elimina la riga override con `removeOverride` (nessuna copia).
 * @param {Object} props - Proprietà
 * @param {Object} props.overview - DTO overview
 * @param {Object} props.community - Voce di `overview.communities`
 * @param {() => void} [props.onApriGoelett] - Apre il collegamento Goelett
 * @returns {import('react').JSX.Element}
 */
const RigaCommunity = ({ overview, community, onApriGoelett }) => {
  const [aperta, setAperta] = useState(false)
  const [conferma, setConferma] = useState(false)
  const { esegui, inCorso } = useComando()
  const situazione = situazioneCommunity(community, overview.defaultConfiguration)
  const override = community.override
  const modeDefault = formDaCard(overview.defaultConfiguration).mode
  const labelDefault = testoModalita(modeDefault, overview.modes, t).label

  const eredita = async () => {
    const esito = await esegui(
      payloadRimozioneOverride({
        environmentId: overview.environment.id,
        communityPresetId: community.id,
        override
      }),
      { titolo: t.cnSalvato, dettaglio: t.cnSoloNuoveChat }
    )
    if (esito !== 'errore') {
      setConferma(false)
      setAperta(false)
    }
  }

  const capabilitiesAccese = override
    ? CAPABILITY_IDS.filter((id) => override.capabilities?.[id]).map(
        (id) => testoCapability(id, overview.capabilities, t).label
      )
    : []

  return (
    <li
      data-testid={`cn-community-${community.id}`}
      style={{ listStyle: 'none', padding: '12px 0', borderTop: '1px solid var(--border)' }}
    >
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong>{community.label}</strong>
            {community.customerCode && (
              <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                {t.cnCodiceCliente}: {community.customerCode}
              </span>
            )}
            {community.isDefault && <span className="nbadge">{t.cnPresetPredefinito}</span>}
            {!community.active && <span className="nbadge nbadge--warn">{t.cnInattiva}</span>}
          </div>

          {situazione === 'non_ammessa' && (
            <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>
              {motivoNonOverride(community.cannotOverrideReason, t)}
            </span>
          )}
          {situazione === 'eredita' && (
            <span style={{ fontSize: 13 }}>
              {formatta(t.cnEreditaValore, { modalita: labelDefault })}
            </span>
          )}
          {(situazione === 'fissata' || situazione === 'eccezione') && (
            <span style={{ fontSize: 13, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>{testoModalita(override.mode, overview.modes, t).label}</span>
              {!!capabilitiesAccese.length && (
                <span style={{ color: 'var(--ink-500)' }}>+ {capabilitiesAccese.join(', ')}</span>
              )}
              {situazione === 'fissata' ? (
                <span className="nbadge" title={t.cnFissataHint}>
                  {t.cnFissata}
                </span>
              ) : (
                <span className="nbadge nbadge--info">{t.cnEccezione}</span>
              )}
            </span>
          )}
          {situazione === 'fissata' && (
            <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnFissataHint}</span>
          )}
          {override && <StatoCard card={override} />}
          {override && !community.overrideApplicable && (
            <span role="status" style={{ fontSize: 12, color: 'var(--danger)' }}>
              {t.cnOverrideNonApplicata}
            </span>
          )}
        </div>

        {/* Un override rimasto su una community non più ammessa (es. diventata predefinita)
            non è modificabile, ma deve restare rimovibile: il server accetta removeOverride. */}
        {(community.canOverride || override) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {community.canOverride && (
              <button
                type="button"
                className="nbtn nbtn--ghost nbtn--sm"
                aria-expanded={aperta}
                onClick={() => setAperta((old) => !old)}
              >
                {aperta ? t.chiudi : override ? t.cnModificaEccezione : t.cnImpostaEccezione}
              </button>
            )}
            {override && (
              <button
                type="button"
                className="nbtn nbtn--ghost nbtn--sm"
                onClick={() => setConferma(true)}
                disabled={inCorso}
              >
                {t.cnEredita}
              </button>
            )}
          </div>
        )}
      </div>

      {community.canOverride && aperta && (
        <SchedaConfigurazione
          key={`c${community.id}-${override?.revision ?? 'nessuna'}`}
          overview={overview}
          card={override}
          formIniziale={formDaCard(override || overview.defaultConfiguration)}
          communityPresetId={community.id}
          variante="community"
          onSalvato={() => setAperta(false)}
          onApriGoelett={onApriGoelett}
        />
      )}

      {override && (
        <DialogConferma
          visible={conferma}
          onHide={() => setConferma(false)}
          titolo={t.cnEredita}
          descrizione={formatta(t.cnDescEredita, { modalita: labelDefault })}
          etichettaConferma={t.cnEredita}
          inCorso={inCorso}
          onConferma={eredita}
        />
      )}
    </li>
  )
}

/**
 * Sezione «Eccezioni per community» (09 §9.1.5, §1.6, §8.3).
 *
 * In alto le community su cui si può agire (ammesse, o con un'eccezione rimasta da
 * togliere); quelle che non possono avere eccezioni — la predefinita, le inattive, quelle
 * senza codice cliente — stanno in un elenco richiuso, con il motivo.
 * @param {Object} props - Proprietà
 * @param {Object} props.overview - DTO overview
 * @param {() => void} [props.onApriGoelett] - Apre il collegamento Goelett
 * @returns {import('react').JSX.Element}
 */
export const EccezioniCommunity = ({ overview, onApriGoelett }) => {
  const [bloccateAperte, setBloccateAperte] = useState(false)
  const { modificabili, bloccate, conEccezione } = gruppiCommunity(overview)
  const riga = (community) => (
    <RigaCommunity
      key={community.id}
      overview={overview}
      community={community}
      onApriGoelett={onApriGoelett}
    />
  )

  return (
    <section className="ncard" aria-labelledby="cn-titolo-eccezioni">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-users" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title" id="cn-titolo-eccezioni">
            {t.cnEccezioniTitolo}
          </h3>
          <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>{t.cnEccezioniHint}</span>
        </div>
        {!!overview.communities?.length && (
          <div className="ncard__head-actions">
            <span className={`nbadge ${conEccezione ? 'nbadge--info' : ''}`}>
              {formatta(t.cnConteggioEccezioni, { n: conEccezione })}
            </span>
          </div>
        )}
      </div>
      <div className="ncard__body" style={{ display: 'grid', gap: 8 }}>
        {!overview.communities?.length && (
          <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>{t.cnNessunaCommunity}</span>
        )}
        {!!overview.communities?.length && !modificabili.length && (
          <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>
            {t.cnNessunaCommunityAmmessa}
          </span>
        )}
        {!!modificabili.length && (
          <ul style={{ margin: 0, padding: 0 }}>{modificabili.map(riga)}</ul>
        )}
        {!!bloccate.length && (
          <div>
            <button
              type="button"
              className="nbtn nbtn--ghost nbtn--sm"
              aria-expanded={bloccateAperte}
              aria-controls="cn-community-bloccate"
              onClick={() => setBloccateAperte((old) => !old)}
            >
              <i className={`pi ${bloccateAperte ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
              {formatta(t.cnCommunityBloccate, { n: bloccate.length })}
            </button>
            {bloccateAperte && (
              <ul id="cn-community-bloccate" style={{ margin: 0, padding: 0 }}>
                {bloccate.map(riga)}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default EccezioniCommunity
